import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Container, Row, Col } from "../../bootstrap/index.jsx";
import ServerSidePaginationAggrid from "../ServerSidePaginationAggrid.jsx";
import ColumnSelector from "../ColumnSelector.jsx";
import { DATA_TYPE_PROP_MAP } from "../AgGrid/index.jsx";
import { monitoring_list_page_path } from '~/routes';

const LOCAL_STORAGE_KEY = "ProjectSelectorList.hiddenColumns";

const DATA_TYPE_MAP = {
  string: 'text',
  datetime: 'timestamp'
};

const GLOBAL_SEARCH_COLUMN = {
  field: '-',
  colId: 'global_search',
  editable: false,
  hide: true,
  lockVisible: true,
  filter: 'agTextColumnFilter',
  suppressColumnsToolPanel: true
};

const PAGINATION_PAGE_SIZE = 25;

const valueGetter = (params) => {
  params.data ||= {};
  return (params.data[params.colDef.colId] || {})['label'] ?? params.data[params.colDef.colId] ?? '';
};

const selectionValueFormatter = ({ value, colDef: { filterParams } }) => {
  if (Array.isArray(value)) {
    return value.map(val => filterParams.optionMap.get(val) || 'undefined').join(', ');
  }
  return filterParams.optionMap.get(value);
};

const ProjectSelectorList = ({ handleSelectedData, sortedColumns, defaultVisibleColumns, selectedItems = [], rowSelectionMode = 'multiRow' }) => {
  const [externalFilter, setExternalFilter] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const gridRef = useRef(null);
  const searchTimerRef = useRef(null);
  const initialSelectedIdsRef = useRef(new Set(selectedItems.map(item => item.value)));
  const initialLabelsRef = useRef(new Map(selectedItems.map(item => [item.value, item.label])));
  const seenIdsRef = useRef(new Set());

  const [hiddenColumns, setHiddenColumns] = useState(() => {
    const stored = localStorageWithExpiry.getItem(LOCAL_STORAGE_KEY);
    if (stored) return stored;
    return (sortedColumns || [])
      .map(c => String(c.value))
      .filter(id => !(defaultVisibleColumns || []).map(String).includes(id));
  });

  useEffect(() => {
    localStorageWithExpiry.setItem(LOCAL_STORAGE_KEY, hiddenColumns);
  }, [hiddenColumns]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api || !sortedColumns.length) return;
    api.applyColumnState({
      state: sortedColumns.map(c => ({
        colId: String(c.value),
        hide: hiddenColumns.includes(String(c.value))
      }))
    });
  }, [hiddenColumns]);

  const columnDefs = useMemo(() => {
    const cols = (sortedColumns || []).map(c => {
      const dataType = DATA_TYPE_MAP[c.data_type] || c.data_type;
      const filter = DATA_TYPE_PROP_MAP[dataType]?.filter || 'agTextColumnFilter';
      return {
        field: c.label,
        colId: String(c.value),
        editable: false,
        sortable: true,
        filter,
        hide: hiddenColumns.includes(String(c.value)),
        valueGetter,
        ...(c.value === 'project_id' && {
          filter: 'agTextColumnFilter',
          sort: 'asc',
          cellRenderer: (params) => params.data?.project_id?.label ?? '',
        }),
        ...(c.value === 'title' && {
          flex: 1,
          minWidth: 100,
        }),
        ...(c.value === 'payments' && {
          cellRenderer: (params) => {
            const data = valueGetter(params);
            let str = '';
            if (data?.total) {
              Object.keys(data.total).forEach((currency, index) => {
                if (index !== 0) str += ', ';
                str += `${data.paid[currency]} / ${data.total[currency]}`;
                if (currency !== 'none') str += ` ${currency.toUpperCase()}`;
              });
            }
            return <span dangerouslySetInnerHTML={{ __html: str }} />;
          }
        }),
        ...(Array.isArray(c.selection_options) && {
          filter: 'agSetColumnFilter',
          filterParams: {
            values: c.selection_options.map(op => op.value),
            optionMap: new Map(c.selection_options.map(op => [op.value, op.label])),
            valueFormatter: selectionValueFormatter
          },
        }),
      };
    });
    return [GLOBAL_SEARCH_COLUMN, ...cols];
  }, [sortedColumns]);

  const columnOptions = useMemo(
    () => (sortedColumns || []).map(c => ({ label: c.label, value: String(c.value) })),
    [sortedColumns]
  );

  const handleSearch = useCallback((e) => {
    const value = e.target.value;
    setSearchValue(value);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setExternalFilter({ global_search: value });
    }, 200);
  }, []);

  const callbackOnRowSelected = useCallback((rows) => {
    const notYetRenderedIds = Array.from(initialSelectedIdsRef.current)
      .filter(id => !seenIdsRef.current.has(id));

    const selectData = [
      ...notYetRenderedIds.map(id => ({ value: id, label: initialLabelsRef.current.get(id) })),
      ...rows.map(row => ({ value: row.id, label: row.project_id?.label })),
    ];
    handleSelectedData(selectData);
  }, [handleSelectedData]);

  const callbackAfterRequest = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.forEachNode(node => {
      const id = node.data?.id;
      if (id == null) return;
      if (initialSelectedIdsRef.current.has(id) && !seenIdsRef.current.has(id)) {
        node.setSelected(true);
      }
      seenIdsRef.current.add(id);
    });
  }, []);

  return (
    <Container fluid maxHeight className="mt-1">
      <Row className="mb-1">
        <Col size="xs-6" className="pl-0">
          <ColumnSelector
            label={I18n.t("columns")}
            columns={columnOptions}
            hiddenColumns={hiddenColumns}
            setHiddenColumns={setHiddenColumns}
          />
        </Col>
        <Col size="xs-6" className="pr-0">
          <input
            type="search"
            className="form-control input-sm float-right"
            placeholder={I18n.t('search')}
            value={searchValue}
            onChange={handleSearch}
            style={{ width: '220px' }}
            autoComplete={false}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <ServerSidePaginationAggrid
            externalGridRef={gridRef}
            fetchPath={monitoring_list_page_path()}
            externalFilter={externalFilter}
            columnDefs={columnDefs}
            callbackOnRowSelected={callbackOnRowSelected}
            callbackAfterRequest={callbackAfterRequest}
            defaultGridOptions={{
              rowSelection: {
                mode: rowSelectionMode,
                checkboxes: true,
                headerCheckbox: true,
                selectAll: 'currentPage',
                enableClickSelection: false,
              },
              pagination: true,
              paginationPageSize: PAGINATION_PAGE_SIZE,
              paginationPageSizeSelector: [10, 25, 50],
              cacheBlockSize: PAGINATION_PAGE_SIZE,
              domLayout: 'normal',
            }}
            style={{ height: 'calc(100vh - 360px)' }}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default ProjectSelectorList;
